// Markup shared by every finance type: the "Get exact terms" dialog (its
// behaviour is apply.ts; wording that differs comes in a DialogWording) and
// the admin preview's sign-in form.
import { escapeHtml } from './format';
import type { PublicBrand } from './types';

const CLOSE_SVG = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

function tip(text: string, about: string, extra = ''): string {
  return `<button type="button" class="info-tip" ${extra} data-tip="${escapeHtml(text)}" aria-label="More about ${about}">?</button>`;
}

/** Wording that differs between finance types. */
export interface DialogWording {
  addressLabel: string;
  timescaleQuestion: string;
  commentsPlaceholder: string;
  /** Bridging's mixed portfolio: more than one security property. */
  portfolio: boolean;
}

export const BRIDGING_DIALOG: DialogWording = {
  addressLabel: 'Security property address',
  timescaleQuestion: 'When do you need to complete?',
  commentsPlaceholder: 'Optional — e.g. planning status, tenancy, timescale pressure…',
  portfolio: true,
};

export const DEV_FINANCE_DIALOG: DialogWording = {
  addressLabel: 'Site address',
  timescaleQuestion: 'When do you need the facility?',
  commentsPlaceholder: 'Optional — e.g. number of units, planning reference, timescale pressure…',
  portfolio: false,
};

export function dialogHtml(brand: PublicBrand, form: { applicantRoles: [string, string][]; titles: string[]; timescales: string[] }, wording: DialogWording): string {
  const phoneRow = (id: string, label: string) => `<div class="phone-row"><input class="control dial" type="text" id="${id}-dial" autocomplete="off" value="🇬🇧 +44" aria-label="Country dialling code" /><input class="control" type="tel" id="${id}" ${label} /></div>`;
  return `
<div id="apply-dialog-backdrop" class="dialog-backdrop hidden">
  <div class="dialog" role="dialog" aria-modal="true" aria-labelledby="apply-dialog-title">
    <button type="button" class="dialog-close" id="apply-dialog-close" aria-label="Close">${CLOSE_SVG}</button>

    <div id="apply-step">
    <h3 class="dialog-title" id="apply-dialog-title">Get exact terms</h3>
    <p id="apply-line" class="text-muted" style="font-size:15px;margin-top:16px"></p>

    <div id="selected-product-card" class="selected-card hidden" style="margin-top:16px">
      <p class="eyebrow">Selected product</p>
      <div id="selected-product-code" class="selected-name"></div>
      <div id="selected-product-summary" class="selected-summary"></div>
    </div>

    <form id="wizard-form" class="dialog-form" novalidate style="margin-top:16px">
      <div class="field" style="max-width:320px">
        <label for="input-who">Your status</label>
        <select class="control" id="input-who">${form.applicantRoles.map(([v, l]) => `<option value="${v}">${escapeHtml(l)}</option>`).join('')}</select>
      </div>

      <div id="client-details-section" class="client-details">
        <h4 class="section-heading hidden" id="client-details-title">Client details</h4>
        <label class="consent hidden" id="call-first-field">
          <input type="checkbox" id="input-call-first" />
          <span>Tick here if you would like us to call you before contacting your client</span>
        </label>
        <div class="name-row">
          <div class="field">
            <label for="input-title">Title</label>
            <select class="control" id="input-title" autocomplete="honorific-prefix"><option value="">Select</option>${form.titles.map((t) => `<option>${escapeHtml(t)}</option>`).join('')}</select>
          </div>
          <div class="field"><label for="input-first-name">First name</label><input class="control" type="text" id="input-first-name" autocomplete="given-name" /></div>
          <div class="field"><label for="input-surname">Surname</label><input class="control" type="text" id="input-surname" autocomplete="family-name" /></div>
        </div>
        <div class="row2">
          <div class="field">
            <div class="label-row"><span class="label-group"><label for="input-phone">Phone</label>${tip("Please enter the borrower's telephone number.", 'phone number', 'id="phone-info-tip" hidden')}</span></div>
            ${phoneRow('input-phone', 'autocomplete="tel-national"')}
          </div>
          <div class="field">
            <div class="label-row"><span class="label-group"><label for="input-email">Email</label>${tip("Please enter the borrower's email here so that we can request further information from your client.", 'email', 'id="email-info-tip" hidden')}</span></div>
            <input class="control" type="email" id="input-email" autocomplete="email" />
          </div>
          <div class="field span2 hidden" id="company-name-field">
            <label for="input-company-name">Company name <span class="optional">(optional)</span></label>
            <input class="control" type="text" id="input-company-name" autocomplete="off" placeholder="Jane Okonkwo Property Ltd" />
            <div id="company-sic-codes" class="sic-codes hidden"></div>
          </div>
        </div>
      </div>

      <div class="field">
        <label for="input-timescale">${escapeHtml(wording.timescaleQuestion)}</label>
        <select class="control" id="input-timescale"><option value="">Select</option>${form.timescales.map((t) => `<option>${escapeHtml(t)}</option>`).join('')}</select>
      </div>

      <div class="field">
        <label for="input-address" id="input-address-label">${escapeHtml(wording.addressLabel)}</label>
        <div class="address-row">
          <input class="control" type="text" id="input-address" placeholder="Start typing an address…" autocomplete="off" />
          ${wording.portfolio ? `<div class="affix-field address-value hidden" id="input-address-value-wrap"><span class="affix" aria-hidden="true">£</span><input class="control" type="text" inputmode="numeric" autocomplete="off" id="input-address-value" placeholder="Value" aria-label="Value of security property 1" /></div>` : ''}
        </div>
        <p class="hint">Optional — helps a broker start valuation checks sooner.</p>
        <div class="property-facts" id="property-facts" hidden aria-live="polite"></div>
      </div>

      ${wording.portfolio ? `      <div id="portfolio-properties" class="portfolio-properties hidden">
        <ol class="portfolio-rows" id="portfolio-rows"></ol>
        <button type="button" class="btn btn-soft btn-sm" id="add-portfolio-property-btn">+ Add another security property</button>
        <p class="hint">Total of values entered: <strong id="portfolio-total">£0</strong> · property value in your estimate: <strong id="portfolio-estimate">£0</strong></p>
      </div>` : ''}

      <div class="field" id="comments-field">
        <label for="input-comments">Anything else that would help us give an accurate estimate?</label>
        <textarea class="control" id="input-comments" rows="3" placeholder="${escapeHtml(wording.commentsPlaceholder)}"></textarea>
      </div>

      <div class="popdown-wrap" id="popdown-wrap">
        <div class="popdown-inner">
          <div id="introducer-panel" class="introducer-panel">
            <h4 id="introducer-panel-title">Introducer details</h4>
            <p class="hint" id="intro-hint">Already registered as an introducer? Log in and your details are filled in for you.</p>
            <div class="pills" role="group" aria-label="Introducer account" id="intro-mode" style="margin:12px 0 16px">
              <button type="button" class="pill" data-intro-mode="login" aria-pressed="false">I'm registered - log in</button>
              <button type="button" class="pill" data-intro-mode="register" aria-pressed="false">I'm new - register</button>
            </div>
            <div id="intro-login" class="hidden">
              <div class="row2">
                <div class="field"><label for="intro-login-email">Email address</label><input class="control" type="email" id="intro-login-email" autocomplete="username" /></div>
                <div class="field"><label for="intro-login-password">Password</label><input class="control" type="password" id="intro-login-password" autocomplete="current-password" /></div>
              </div>
              <div style="display:flex;flex-wrap:wrap;align-items:center;gap:8px 16px;margin-top:12px">
                <button type="button" class="btn btn-primary btn-sm" id="intro-login-btn">Log in</button>
                <button type="button" class="link-btn" id="intro-forgot-btn">Forgot your password?</button>
              </div>
              <p class="form-error hidden" id="intro-login-error" role="alert" style="margin-top:8px"></p>
              <p class="hint hidden" id="intro-forgot-msg" role="status" style="margin-top:8px">If that email has an introducer account, we've sent a link to reset the password.</p>
            </div>
            <div id="intro-logged-in" class="hidden" role="status">
              <p style="font-size:15px">Logged in as <strong id="intro-logged-name"></strong><span id="intro-logged-company"></span> (<span id="intro-logged-email"></span>). We'll use your saved details for this case. <button type="button" class="link-btn" id="intro-logout-btn">Not you? Log out</button></p>
            </div>
            <div id="intro-register" class="hidden">
              <div class="row2">
                <div class="field"><label for="input-intro-first-name">First name</label><input class="control" type="text" id="input-intro-first-name" /></div>
                <div class="field"><label for="input-intro-surname">Surname</label><input class="control" type="text" id="input-intro-surname" /></div>
                <div class="field"><label for="input-intro-email">Email address</label><input class="control" type="email" id="input-intro-email" /></div>
                <div class="field"><label for="input-intro-phone">Phone number</label>${phoneRow('input-intro-phone', '')}</div>
                <div class="field span2"><label for="input-intro-company-name">Company name</label><input class="control" type="text" id="input-intro-company-name" autocomplete="off" /></div>
                <div class="field span2"><label for="input-intro-registration-number">Company registration number <span class="optional">(optional)</span></label><input class="control" type="text" id="input-intro-registration-number" /></div>
                <div class="field span2"><label for="input-intro-company-address">Company address</label><input class="control" type="text" id="input-intro-company-address" placeholder="Start typing an address…" autocomplete="off" /></div>
                <div class="field span2 hidden" id="introducer-fca-field"><label for="input-intro-fca-number">FCA number <span class="optional">(optional)</span></label><input class="control" type="text" id="input-intro-fca-number" /></div>
              </div>
              <p class="hint" style="margin-top:10px">We'll email you a link to confirm your email address and set a password - your introducer account is ready as soon as you do.</p>
            </div>
          </div>
        </div>
      </div>

      <label class="consent">
        <input type="checkbox" id="input-consent-data-sharing" />
        <span>I agree to the <a href="${escapeHtml(brand.privacyUrl)}" target="_blank" rel="noopener">Privacy Policy</a> and give permission for my details to be shared with lenders in order to obtain quotes. Soft credit searches may be carried out, which will not affect my credit rating — we'll let you know before any hard credit search is carried out.</span>
      </label>
      <label class="consent">
        <input type="checkbox" id="input-consent" />
        <span>I'm happy to receive marketing about other products and services by email or phone. You can withdraw consent at any time.</span>
      </label>

      <button class="btn btn-primary btn-block btn-lg" type="submit" id="submit-btn">Submit my pre-application</button>
      <p class="hint">We'll confirm exact terms with the lender before anything is submitted to them. No credit search is run at this stage.</p>
      <div id="apply-error" class="form-error hidden" role="alert"></div>
    </form>
    </div>

    <div id="thanks-step" class="thanks hidden" aria-live="polite"></div>
  </div>
</div>`;
}

export function adminLoginHtml(): string {
  return `
<form class="admin-login" id="admin-login-form">
  <h2>Internal preview — sign in</h2>
  <p class="hint">Real lender names. Use your Stirling Partners admin login.</p>
  <div class="field"><label for="admin-email">Email</label><input class="control" type="email" id="admin-email" autocomplete="username" required /></div>
  <div class="field"><label for="admin-password">Password</label><input class="control" type="password" id="admin-password" autocomplete="current-password" required /></div>
  <button class="btn btn-primary" type="submit">Sign in</button>
  <p class="form-error hidden" id="admin-login-error" role="alert"></p>
</form>`;
}
